import QRCode from 'qrcodejs';
import jsQR from 'jsqr';
import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { QRCODE_SIZE } from '../config';

const messages = defineMessages({
  invalid_id: {
    id: 'error_invalid_id',
    defaultMessage: 'Please enter a username, 12-digit ICN, or User/Group ID',
    description: 'Error message'
  }
});

class NewTopicById extends React.PureComponent {
  constructor(props) {
    super(props);

    this.qrCodeRef = React.createRef();
    this.videoRef = React.createRef();
    this.canvasRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.scanTimer = null;
    this.mediaStream = null;

    this.state = {
      groupId: '',
      mode: 'myqr', // 'myqr' | 'scan'
      copied: false,
      scanStatus: '',
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSwitchMode = this.handleSwitchMode.bind(this);
    this.handleCopyId = this.handleCopyId.bind(this);
    this.handleFileScan = this.handleFileScan.bind(this);
  }

  componentDidMount() {
    this.renderMyQRCode();
  }

  componentWillUnmount() {
    this.stopScanner();
  }

  renderMyQRCode() {
    if (this.qrCodeRef.current) {
      this.qrCodeRef.current.innerHTML = '';
      new QRCode(this.qrCodeRef.current, {
        text: this.props.myURI,
        width: QRCODE_SIZE,
        height: QRCODE_SIZE,
      });
    }
  }

  handleSwitchMode(mode) {
    if (this.state.mode === mode) return;
    if (mode === 'scan') {
      this.setState({mode: 'scan', scanStatus: 'Starting camera...'}, () => {
        this.startScanner();
      });
    } else {
      this.stopScanner();
      this.setState({mode: 'myqr', scanStatus: ''}, () => {
        this.renderMyQRCode();
      });
    }
  }

  startScanner() {
    this.stopScanner();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setState({scanStatus: 'Camera not available in this browser. Use "Scan from Image" below.'});
      return;
    }
    navigator.mediaDevices.getUserMedia({video: {facingMode: 'environment'}})
      .then(stream => {
        this.mediaStream = stream;
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream;
          this.videoRef.current.setAttribute('playsinline', true);
          this.videoRef.current.play().catch(() => {});
        }
        this.setState({scanStatus: 'Point camera at a KalTiQ QR code...'});
        this.scanTimer = setInterval(() => this.scanVideoFrame(), 250);
      })
      .catch(() => {
        this.setState({scanStatus: 'Camera permission denied or unavailable. Use "Scan from Image" below.'});
      });
  }

  stopScanner() {
    if (this.scanTimer) {
      clearInterval(this.scanTimer);
      this.scanTimer = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
  }

  scanVideoFrame() {
    const video = this.videoRef.current;
    const canvas = this.canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      return;
    }
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });
    if (code && code.data) {
      const scanned = code.data.trim();
      if (scanned) {
        this.stopScanner();
        this.props.onSubmit(scanned);
      }
    }
  }

  handleFileScan(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code && code.data) {
          this.stopScanner();
          this.props.onSubmit(code.data.trim());
        } else {
          this.props.onError('No valid QR code found in the selected image.', 'err');
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  handleCopyId() {
    const id = this.props.myUserId || (this.props.myURI || '').replace(/^(kaltiq|kaltiq):id\//i, '');
    if (!id) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(id).then(() => {
        this.setState({copied: true});
        setTimeout(() => this.setState({copied: false}), 2000);
      });
    }
  }

  handleChange(e) {
    this.setState({groupId: e.target.value});
  }

  handleKeyPress(e) {
    if (e.key === 'Enter') {
      this.handleSubmit(e);
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    const name = (this.state.groupId || '').trim();
    if (name) {
      this.props.onSubmit(name);
    } else {
      this.props.onError(this.props.intl.formatMessage(messages.invalid_id), 'err');
    }
  }

  render() {
    const myId = this.props.myUserId || (this.props.myURI || '').replace(/^(kaltiq|kaltiq):id\//i, '');
    return (
      <div className="panel-form">
        <div className="panel-form-row">
          <input
            type="text"
            placeholder="Username, Email ID, 12-Digit ICN, or Serial No."
            value={this.state.groupId}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyPress}
            required
            autoFocus
          />
        </div>
        <div className="dialog-buttons">
          <button className="primary" onClick={this.handleSubmit}>
            <FormattedMessage id="button_subscribe" defaultMessage="Start Conversation"
              description="Button [Subscribe]" />
          </button>
        </div>

        <div style={{display: 'flex', gap: '8px', width: '100%', marginTop: '14px', marginBottom: '10px'}}>
          <button
            type="button"
            onClick={() => this.handleSwitchMode('myqr')}
            style={{
              flex: 1,
              padding: '9px 12px',
              borderRadius: '10px',
              border: '1px solid #cbd5e1',
              background: this.state.mode === 'myqr' ? '#4f46e5' : '#f1f5f9',
              color: this.state.mode === 'myqr' ? '#ffffff' : '#1e293b',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
            <i className="material-icons" style={{fontSize: '18px'}}>qr_code_2</i>
            <span>My QR Code</span>
          </button>
          <button
            type="button"
            onClick={() => this.handleSwitchMode('scan')}
            style={{
              flex: 1,
              padding: '9px 12px',
              borderRadius: '10px',
              border: '1px solid #cbd5e1',
              background: this.state.mode === 'scan' ? '#4f46e5' : '#f1f5f9',
              color: this.state.mode === 'scan' ? '#ffffff' : '#1e293b',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
            <i className="material-icons" style={{fontSize: '18px'}}>qr_code_scanner</i>
            <span>Scan QR Code</span>
          </button>
        </div>

        {this.state.mode === 'myqr' ? (
          <div className="panel-form-column" style={{alignItems: 'center'}}>
            <label className="small">
              <FormattedMessage id="label_scan_id" defaultMessage="Scan my QR code to connect:" description="Label [Scan my ID:]" />
            </label>
            <div className="qr-code" ref={this.qrCodeRef} style={{marginTop: '8px'}} />
            {myId ? (
              <div
                onClick={this.handleCopyId}
                title="Click to copy User ID"
                style={{
                  marginTop: '10px',
                  padding: '6px 12px',
                  background: '#eef2ff',
                  border: '1px solid #c7d2fe',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: '#1e293b',
                  cursor: 'pointer'
                }}>
                My ID: <strong>{myId}</strong> {this.state.copied ? '✓ Copied!' : '(Copy)'}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="panel-form-column" style={{alignItems: 'center', width: '100%'}}>
            <label className="small" style={{marginBottom: '6px', textAlign: 'center'}}>
              {this.state.scanStatus || 'Point camera at a KalTiQ QR code'}
            </label>
            <div style={{
              width: '240px',
              height: '240px',
              background: '#0f172a',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #4f46e5'
            }}>
              <video
                ref={this.videoRef}
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
                muted
                playsInline
              />
              <canvas ref={this.canvasRef} style={{display: 'none'}} />
            </div>
            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              style={{display: 'none'}}
              onChange={this.handleFileScan}
            />
            <button
              type="button"
              onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}
              style={{
                marginTop: '10px',
                padding: '7px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#1e293b',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
              <i className="material-icons" style={{fontSize: '18px'}}>image</i>
              <span>Scan QR from Image</span>
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default injectIntl(NewTopicById);
